import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';

/** 工单类型（`sv_workorder.order_type`）。 */
export type WorkorderType = 'REPAIR' | 'INSTALL' | 'COMPLAINT' | 'CONSULT';

/** 工单状态机：NEW→ASSIGNED→PROCESSING→WAIT_PART/FINISHED→VERIFIED→CLOSED。 */
export type WorkorderStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'PROCESSING'
  | 'WAIT_PART'
  | 'FINISHED'
  | 'VERIFIED'
  | 'CLOSED'
  | 'CANCELED';

export type WorkorderAction = Exclude<WorkorderStatus | 'ASSIGNED', 'NEW'> | 'CANCELED';

export interface WorkorderRow {
  id: Id;
  bizNo: string;
  orgId: Id;
  deptId: Id;
  deptName: string | null;
  orderType: string;
  materialId: Id;
  materialName: string | null;
  supplierId: Id;
  supplierName: string | null;
  faultDesc: string | null;
  picIds: string | null;
  urgentLevel: string;
  assignUserId: Id;
  assignName: string | null;
  expectFinishAt: string | null;
  status: string;
  finishAt: string | null;
  solution: string | null;
  feeAmount: number;
  partsUsedJson: string | null;
  satisfactionId: Id;
  /** SLA 时限快照（EMERGENCY 2h / URGENT 8h / NORMAL 24h）。 */
  slaHours: number;
  isOvertime: boolean;
  bizDate: string;
  createAt: string;
}

export interface WorkorderListQuery {
  status?: string;
  orderType?: string;
  lastId?: Id;
  size?: number;
}

export interface WorkorderCreateRequest {
  deptId?: Id;
  deptName?: string;
  orderType?: WorkorderType;
  materialId?: Id;
  materialName?: string;
  supplierId?: Id;
  supplierName?: string;
  /** 不少于 5 字。 */
  faultDesc: string;
  picIds?: string;
  urgentLevel?: 'NORMAL' | 'URGENT' | 'EMERGENCY';
  expectFinishAt?: string;
}

export interface WorkorderCreateResult {
  id: Id;
  bizNo: string;
  status: string;
  slaHours: number;
  expectFinishAt: string;
  /** 配套会话线程号（`SV_WORKORDER-{id}`），可直接在客服中心回复。 */
  threadNo: string;
}

export interface WorkorderAssignRequest {
  assignUserId: Id;
  assignName?: string;
}

export interface WorkorderHandleRequest {
  action: Exclude<WorkorderStatus, 'NEW' | 'ASSIGNED'>;
  /** `FINISHED` 必填，不少于 5 字。 */
  solution?: string;
  feeAmount?: number;
  partsUsedJson?: string;
}

export interface WorkorderHandleResult {
  id: Id;
  bizNo: string;
  fromStatus: string;
  status: string;
  finishAt: string | null;
  isOvertime: boolean;
}

/** 售后/维修工单（`WX` 单，招标 ★2(3) 实时售后回复 / 免费配件 / 24h 响应）。 */
export function createWorkorderApi(http: HttpClient) {
  return {
    list(query: WorkorderListQuery = {}): Promise<{ items: WorkorderRow[]; hasMore: boolean; nextLastId: Id }> {
      return http.request<{ items: WorkorderRow[]; hasMore: boolean; nextLastId: Id }>({
        path: '/wl/workorder',
        query: toQuery(query)
      });
    },
    detail(id: Id): Promise<WorkorderRow> {
      return http.request<WorkorderRow>({ path: `/wl/workorder/${id}` });
    },
    create(body: WorkorderCreateRequest): Promise<WorkorderCreateResult> {
      return http.request<WorkorderCreateResult>({ path: '/wl/workorder', method: 'POST', body });
    },
    assign(id: Id, body: WorkorderAssignRequest): Promise<{ id: Id; assignUserId: Id; status: string }> {
      return http.request<{ id: Id; assignUserId: Id; status: string }>({
        path: `/wl/workorder/${id}/assign`,
        method: 'POST',
        body
      });
    },
    handle(id: Id, body: WorkorderHandleRequest): Promise<WorkorderHandleResult> {
      return http.request<WorkorderHandleResult>({
        path: `/wl/workorder/${id}/handle`,
        method: 'POST',
        body
      });
    }
  };
}

export type WorkorderApi = ReturnType<typeof createWorkorderApi>;
